/**
 * MessageSettings — configure tone, example message and custom instructions.
 * Fetches current config on mount and saves via PUT /api/settings/messages.
 */

import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FieldHint } from '@/components/field-hint'
import { useApi } from '@/hooks/use-api'
import type { MessageConfig, MessageTone } from '@dealcloser/shared'
import { DEFAULT_MESSAGES } from '@dealcloser/shared'

const messageKeys = { config: ['settings', 'messages'] as const }

const TONE_OPTIONS: readonly { readonly value: MessageTone; readonly label: string }[] = [
  { value: 'professional_casual', label: 'Professional Casual' },
  { value: 'casual', label: 'Casual' },
  { value: 'professional', label: 'Professional' },
  { value: 'formal', label: 'Formal' },
] as const

const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-subtle-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none'

export default function MessageSettings() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const [form, setForm] = useState<MessageConfig>(DEFAULT_MESSAGES)
  const [savedSuccess, setSavedSuccess] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: messageKeys.config,
    queryFn: async (): Promise<MessageConfig> => {
      const res = await apiFetch<MessageConfig>('/api/settings/messages')
      if (!res.success || !res.data) return DEFAULT_MESSAGES
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const saveMutation = useMutation({
    mutationFn: async (config: MessageConfig): Promise<void> => {
      const res = await apiFetch('/api/settings/messages', { method: 'PUT', body: config })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messageKeys.config })
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  const isSaving = saveMutation.isPending
  const saveError = saveMutation.error instanceof Error ? saveMutation.error.message : null

  return (
    <div className="space-y-6">
      {/* Tone & Style */}
      <Card>
        <CardHeader>
          <CardTitle>Tone & Style</CardTitle>
          <CardDescription>Control how the AI writes messages on your behalf.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="msg-tone">Message Tone</Label>
              <FieldHint label="How does message tone work?">
                Sets the overall register the AI writes in. Casual reads like a friendly DM; formal leans more polished. Doesn't override your voice samples, just biases the baseline.
              </FieldHint>
            </div>
            <Select
              value={form.messageTone}
              onValueChange={(value: MessageTone) =>
                setForm((prev) => ({ ...prev, messageTone: value }))
              }
              disabled={isLoading || isSaving}
            >
              <SelectTrigger id="msg-tone">
                <SelectValue placeholder="Select tone" />
              </SelectTrigger>
              <SelectContent>
                {TONE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="msg-example">Example Message</Label>
              <FieldHint label="How is the example message used?">
                A reference DM in your own voice. The AI mirrors its cadence, vocabulary, and rhythm, never copies it verbatim. One good example beats a long instruction.
              </FieldHint>
            </div>
            <textarea
              id="msg-example"
              rows={4}
              placeholder="Paste a message that represents your ideal outreach style..."
              value={form.exampleMessage}
              onChange={(e) => setForm((prev) => ({ ...prev, exampleMessage: e.target.value }))}
              disabled={isLoading || isSaving}
              className={TEXTAREA_CLASS}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="msg-instructions">Custom AI Instructions</Label>
              <FieldHint label="What are custom AI instructions?">
                Extra do's and don'ts injected into every generation prompt. Use this for things like "don't mention competitors by name" or "always reference our Series B raise". Short, specific lines work best.
              </FieldHint>
            </div>
            <textarea
              id="msg-instructions"
              rows={3}
              placeholder="Any special instructions for the AI when generating messages..."
              value={form.customInstructions}
              onChange={(e) => setForm((prev) => ({ ...prev, customInstructions: e.target.value }))}
              disabled={isLoading || isSaving}
              className={TEXTAREA_CLASS}
            />
          </div>
        </CardContent>
      </Card>

      {/* Pacing: rate limits and follow-up delays used to live here, but
          nothing read them (2026-09-26 QA finding). Volume is platform
          managed and follow-up timing is per campaign step. */}
      <Card>
        <CardHeader>
          <CardTitle>Pacing &amp; Follow-Up Timing</CardTitle>
          <CardDescription>
            Daily volume and sending hours are managed by the platform to keep your LinkedIn
            account safe. Follow-up timing is set per campaign, on each step of its sequence
            (Campaigns, open a campaign, Edit).
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={() => saveMutation.mutate(form)} disabled={isSaving || isLoading}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Message Settings'
          )}
        </Button>
        {savedSuccess && (
          <div className="flex items-center gap-1.5 text-sm text-emerald-300 ">
            <CheckCircle2 className="h-4 w-4" />
            Saved successfully
          </div>
        )}
        {saveError && <p className="text-sm text-destructive">{saveError}</p>}
      </div>
    </div>
  )
}
